/* eslint-disable jsx-a11y/no-autofocus */
import { useState } from "react";
import PropTypes from "prop-types";
import { useField } from "formik";

import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import FilledInput from "@material-ui/core/FilledInput";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import FormHelperText from "@material-ui/core/FormHelperText";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

import isMSLegacy from "common/utils/is-ms-legacy";
import passwordStyles from "./password.styles";

const useStyles = makeStyles(passwordStyles);

function PasswordField({
  variant,
  label,
  margin,
  fullWidth,
  required,
  className,
  inputProps,
  autoFocus,
  showVisibilityToggle,
  ...props
}) {
  // eslint-disable-next-line no-unused-vars
  const [showPassword, setShowPassword] = useState(false);
  const [field, meta] = useField(props);

  const { name, onBlur, onChange, value } = field;

  const classes = useStyles();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <FormControl
      variant={variant}
      margin={margin}
      fullWidth={fullWidth}
      required={required}
      className={className}
    >
      <InputLabel htmlFor={name} error={meta.touched && Boolean(meta.error)}>
        {label}
      </InputLabel>
      <FilledInput
        autoFocus={autoFocus}
        id={name}
        type={showPassword ? "text" : "password"}
        error={meta.touched && Boolean(meta.error)}
        classes={{ input: classes.input }}
        inputProps={{ autoComplete: "off", ...inputProps }}
        endAdornment={
          isMSLegacy() || !showVisibilityToggle ? null : (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {showPassword ? (
                  <Visibility />
                ) : (
                  <VisibilityOff
                    className={classes.passwordVisibilityOffIcon}
                  />
                )}
              </IconButton>
            </InputAdornment>
          )
        }
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        value={value}
      />
      <FormHelperText
        variant={variant}
        error={meta.touched && Boolean(meta.error)}
      >
        {meta.touched ? meta.error : ""}
      </FormHelperText>
    </FormControl>
  );
}

PasswordField.propTypes = {
  variant: PropTypes.oneOf(["standard", "outlined", "filled"]).isRequired,
  label: PropTypes.string.isRequired,
  margin: PropTypes.oneOf(["none", "dense", "normal"]).isRequired,
  fullWidth: PropTypes.bool,
  required: PropTypes.bool,
  className: PropTypes.string,
  inputProps: PropTypes.objectOf(PropTypes.string),
  autoFocus: PropTypes.bool,
  showVisibilityToggle: PropTypes.bool,
};

PasswordField.defaultProps = {
  fullWidth: false,
  required: false,
  className: "",
  inputProps: null,
  autoFocus: false,
  showVisibilityToggle: true,
};

export default PasswordField;
